// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "@/features/auth/authThunks";

// React Utils
import { useState, useRef } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import SignInImage from "@/assets/FormSignIn.webp";
import BgImage from "@/assets/background.webp";
import AppLayout from "@/assets/appwhoop_app_layout.webp";
import LogoAppWhoop from "@/assets/appwhoop_ticket_logo.webp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Framer Motion
import { motion as Motion, AnimatePresence } from "framer-motion";
import { loginReset } from "@/features/auth/authSlice";

export default function SignIn() {
  const dispatch = useDispatch();
  const { isAuthenticated, error, isLoading } = useSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const passwordInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ email: "", password: "", remember: false });
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;

  const validate = () => {
    const newErrors = {};
    if (!form.email) newErrors.email = "El email es obligatorio";
    if (!form.password) newErrors.password = "La contraseña es obligatoria";
    if (form.password && form.password.length < 8) newErrors.password = "La contraseña debe tener al menos 8 caracteres";
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) newErrors.email = "El email no es válido";
    if (form.email && form.email.length > 50) newErrors.email = "El email no puede tener más de 50 caracteres";
    if (form.password && form.password.length > 50) newErrors.password = "La contraseña no puede tener más de 50 caracteres";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      try {
        const result = dispatch(loginUser({ username: form.email, password: form.password }));
        await result.unwrap();
        navigate(
          from?.startsWith("/evento/") || from?.startsWith("/experiencias/") ? from : "/"
        );
      } catch (error) {
        console.error("Login error:", error);
      }
    }
  };

  const handleGoogleSignIn = () => {
    if (from?.startsWith("/evento/") || from?.startsWith("/experiencias/")) {
      sessionStorage.setItem("auth_return_to", from);
    }
    const redirectUri = `${window.location.origin}/auth/callback`;
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    window.location.href = `${cognitoDomain}/oauth2/authorize?client_id=${clientId}&response_type=code&scope=openid+email+profile&redirect_uri=${encodeURIComponent(redirectUri)}&identity_provider=Google&prompt=select_account`;
  };

  if (isAuthenticated) return <Navigate to="/" replace />;

  return (
    <div className="relative flex h-screen w-screen overflow-hidden">
      {/* Fondo desktop — grid de eventos con gradiente oscuro */}
      <div className="absolute inset-0 hidden md:block bg-aw-bluedark">
        <img src={BgImage} alt="" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/92 via-black/70 to-black/35" />
      </div>

      {/* Panel formulario — ancho fijo en desktop, pantalla completa en mobile */}
      <div className="relative z-10 flex-1 md:flex-none md:w-[500px] flex flex-col justify-center px-8 py-10 bg-aw-bluedark md:bg-aw-bluedark/80 md:backdrop-blur-sm text-white overflow-y-auto">
        {/* Imagen de fondo solo en mobile */}
        <div className="absolute inset-0 md:hidden">
          <img src={SignInImage} alt="Sign In" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-aw-bluedark-75 backdrop-blur-[2px]" />
        </div>

        <div className="relative px-0 flex flex-col flex-1 justify-center">
          <span onClick={() => navigate(-1)} className="absolute left-0 top-0 cursor-pointer">
            <FontAwesomeIcon
              icon="fa-solid fa-arrow-left"
              className="text-white hover:text-gray-400 duration-200 font-bold text-lg"
            />
          </span>

          <div className="w-28 mb-4">
            <img src={LogoAppWhoop} alt="AppWhoop Logo" className="w-full h-auto object-contain" />
          </div>
          <h2 className="text-2xl font-bold mb-1">Inicia sesión con tu perfil</h2>
          <p className="text-sm text-gray-400 mb-4">Ingresa tus datos para continuar</p>

          {/* ── Sección de login social (prominente) ── */}
          <div className="flex flex-col gap-2 mb-4">
            {/* Google — activo */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 font-semibold py-3 rounded-xl hover:bg-gray-50 hover:scale-95 transition-all duration-200 text-sm shadow-md"
            >
              <FontAwesomeIcon icon="fa-brands fa-google" className="text-[#DB4437] text-base" />
              Continuar con Google
            </button>
            {/* Proveedores futuros */}
            <div className="flex gap-2">
              <button
                type="button"
                disabled
                title="Próximamente"
                className="flex-1 flex items-center justify-center gap-2 bg-white/8 text-white/35 font-medium py-2.5 rounded-xl text-sm cursor-not-allowed border border-white/10"
              >
                <FontAwesomeIcon icon="fa-brands fa-apple" className="text-base" />
                Apple
                <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded-full leading-tight">Próx.</span>
              </button>
              <button
                type="button"
                disabled
                title="Próximamente"
                className="flex-1 flex items-center justify-center gap-2 bg-white/8 text-white/35 font-medium py-2.5 rounded-xl text-sm cursor-not-allowed border border-white/10"
              >
                <FontAwesomeIcon icon="fa-brands fa-facebook" className="text-base" />
                Facebook
                <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded-full leading-tight">Próx.</span>
              </button>
            </div>
          </div>

          {/* Divisor */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-white/15" />
            <span className="text-xs text-white/50">o continúa con tu email</span>
            <div className="flex-1 h-px bg-white/15" />
          </div>

          {/* Formulario o estado de error */}
          {error == null ? (
            <form onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  id="email"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  name="email"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl bg-white text-black text-sm focus:outline-none focus:ring-2 focus:ring-aw-bluedark"
                  placeholder="Email"
                />
              </div>
              {errors.email && <span className="text-xs text-red-500">{errors.email}</span>}

              <div className="relative mt-2">
                <label htmlFor="password" className="block text-sm font-medium mb-1">Contraseña</label>
                <div className="relative">
                  <input
                    ref={passwordInputRef}
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={form.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    name="password"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl bg-white text-black text-sm focus:outline-none focus:ring-2 focus:ring-aw-bluedark pr-10"
                    placeholder="Contraseña"
                  />
                  <button
                    type="button"
                    className="h-full w-1/12 absolute top-0 right-1 flex justify-center items-center text-gray-500 hover:text-aw-bluedark focus:outline-none duration-200 px-2"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const input = passwordInputRef.current;
                      const start = input.selectionStart;
                      const end = input.selectionEnd;
                      setShowPassword((prev) => !prev);
                      setTimeout(() => { input.focus(); input.setSelectionRange(start, end); }, 0);
                    }}
                    tabIndex={-1}
                  >
                    <AnimatePresence>
                      <Motion.div
                        key={showPassword ? "eye-slash" : "eye"}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <FontAwesomeIcon
                          icon={showPassword ? "fa-regular fa-eye-slash" : "fa-regular fa-eye"}
                          className="absolute top-3 right-3"
                        />
                      </Motion.div>
                    </AnimatePresence>
                  </button>
                </div>
              </div>
              {errors.password && <span className="text-xs text-red-500">{errors.password}</span>}

              <div className="flex items-center justify-between mt-3 mb-6">
                <div className="flex items-center">
                  <input type="checkbox" id="remember" className="w-4 h-4 rounded accent-aw-green" />
                  <label htmlFor="remember" className="ml-2 block text-sm">Recuérdame</label>
                </div>
                <div className="text-sm">
                  <Link to="/forgot-password" className="text-aw-green hover:underline">
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full text-base bg-aw-green text-black font-semibold py-3 px-4 rounded-xl tracking-wide transition-transform duration-300 hover:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Iniciando sesión..." : "Iniciar Sesión"}
              </button>
            </form>
          ) : (
            <div className="text-amber-500 text-center mt-2">
              <FontAwesomeIcon
                icon="fa-solid fa-user-lock"
                className="text-4xl mx-auto mb-2 animate-bounce"
              />
              <h4 className="text-2xl text-amber-50 font-semibold">{error.message}</h4>
              <button
                onClick={() => dispatch(loginReset())}
                className="mt-4 px-4 py-2 bg-aw-green text-aw-dark rounded-lg font-semibold hover:bg-aw-green/95 hover:scale-95 transition-transform duration-300"
              >
                Volver a Intentar
              </button>
              <span className="text-xs my-4 text-amber-50 duration-500 block mx-auto">
                Si ya cuentas con un código para confirmar tu cuenta,{" "}
                <button
                  onClick={() => { dispatch(loginReset()); setTimeout(() => { navigate("/confirmar-usuario"); }, 1000); }}
                  className="text-aw-green hover:underline font-semibold ml-1"
                >
                  haz clic aquí
                </button>
              </span>
              <span className="text-xs mt-4 text-amber-50 duration-500 block mx-auto">
                Si no has recibido el código de confirmación o{" "}
                <span className="font-semibold">ha expirado</span>
                <button
                  onClick={() => { dispatch(loginReset()); setTimeout(() => { navigate("/reenviar-codigo-confirmar-usuario"); }, 1000); }}
                  className="text-amber-400 hover:underline font-semibold ml-1"
                >
                  haz clic aquí
                </button>
              </span>
            </div>
          )}

          <hr className="my-5 border-white/10" />
          <div className="text-center">
            <p className="text-sm text-gray-300">
              ¿No tienes una cuenta?{" "}
              <Link
                to="/signup"
                state={from ? { from } : undefined}
                className="text-aw-green hover:text-aw-green/80 hover:underline font-semibold"
              >
                Regístrate Gratis
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Panel branding — solo desktop */}
      <div className="hidden md:flex flex-1 relative z-10 flex-row items-center justify-center px-12 gap-10">
        <div className="flex-1 min-w-0">
          <p className="text-aw-green font-semibold text-sm uppercase tracking-widest mb-4">
            AppWhoop Ticket
          </p>
          <h1 className="text-5xl font-extrabold text-white leading-tight drop-shadow-lg">
            Descubre y vive<br />los mejores eventos
          </h1>
          <p className="text-white/55 text-lg mt-4 leading-relaxed">
            Compra tus tickets de forma segura y sencilla.<br />
            Miles de eventos te esperan.
          </p>
        </div>
        <div className="relative w-6/12 shrink-0 rotate-1">
          <div className="absolute -inset-4 bg-aw-green/10 rounded-3xl blur-2xl" />
          <img
            src={AppLayout}
            alt="AppWhoop App"
            className="relative w-full h-auto rounded-2xl"
          />
        </div>
      </div>
    </div>
  );
}
